import { useMemo, useCallback } from 'react';
import { ChoiceGroupProps } from '../../types';

export type HookPayload<T> = Pick<ChoiceGroupProps<T>, 'multiple' | 'onChange' | 'value' | 'size' | 'fill'>;

const useChoiceGroup = <T>({ value: groupValue, onChange, multiple, size, fill }: HookPayload<T>) => {
    const onItemClick = useCallback(
        (itemValue: T) => {
            if (multiple && Array.isArray(groupValue)) {
                const isCurrentlySelected = groupValue.includes(itemValue);
                const newGroupValue = isCurrentlySelected
                    ? groupValue.filter((value) => value !== itemValue)
                    : groupValue.concat(itemValue);

                onChange(newGroupValue as T & T[]);
                return;
            }

            const isCurrentlySelected = itemValue === groupValue;
            const newValue = isCurrentlySelected ? null : itemValue;

            onChange(newValue as T & T[]);
        },
        [groupValue, multiple, onChange]
    );

    return useMemo(
        () => ({
            value: groupValue,
            onItemClick,
            multiple,
            size,
            fill,
        }),
        [groupValue, onItemClick, multiple, size, fill]
    );
};

export default useChoiceGroup;
